// Categoria de um grupo (ou produto) do cardápio, pelo nome. Serve ao "Peça
// também" da sacola (CartUpsell), para sugerir o que falta no pedido.
//
// As regras saíram dos ~400 nomes de grupo distintos usados pelas lojas
// Pallas (set/2026). A ordem importa: o primeiro que casar vence. Por isso
// "Pizzas Doces" é prato (e não sobremesa), "Drinks não alcoólicos" é bebida
// (e não álcool) e "Adicionais Açaí" é adicional.

import { normalizeSearchText } from "./searchProducts";

export const CATEGORY = Object.freeze({
  DRINK: "bebida",
  ALCOHOL: "alcool",
  DESSERT: "sobremesa",
  SIDE: "acompanhamento",
  MAIN: "prato",
  COMBO: "combo",
  EXTRA: "adicional",
  // nome que não diz nada ("Fabricação Própria", "Diversos"): a categoria sai
  // do nome do produto
  GENERIC: "generico",
  // não é para oferecer: testes, taxas, embalagens, cigarro, mercearia...
  INTERNAL: "interno",
});

// Sem acento, minúsculo, sem o que estiver entre parênteses (há grupos como
// "Espressos (CLIQUE PARA VISUALIZAR OS ADICIONAIS)").
function normalize(text) {
  return normalizeSearchText((text || "").replace(/\(.*?\)/g, " ")).replace(
    /\s+/g,
    " ",
  );
}

// Casa no começo de palavra: "cervej" pega "cerveja" e "cervejas".
const words = (list) => new RegExp(`\\b(?:${list.join("|")})`);

// Nomes genéricos só quando são o nome inteiro: "Diversos" é genérico,
// "Lanches Variados" é prato.
const GENERIC_NAMES =
  /^(?:fabricacao propria|diversos|outros|geral|variados|terceiros|cozinha|produtos|itens|cardapio|menu|todos|entrega|delivery|restaurante|novidades|kits?|unidades|presente|viagem)$/;

const RULES = [
  [
    CATEGORY.INTERNAL,
    words([
      "teste",
      "materia prima",
      "embalage",
      "descartave",
      "taxas? de entrega",
      "ifood",
      "nao exibe",
      "respostas?\\b",
      "material de limpeza",
      "cigarro",
      "tabacaria",
      "pet shop",
      "gelos?\\b",
      "carvao",
      "acessorio",
      "couvert",
      "mercearia",
      "mercado",
      "mercadinho",
      "acougue",
      "verduras",
      "laticinio",
      "frios\\b",
      "congelados",
      "conveniencia",
      "emporio",
      "kits? churrasco",
    ]),
  ],
  [
    CATEGORY.EXTRA,
    words([
      "adiciona",
      "adic\\.",
      "adcionais",
      "add lanches",
      "extras?\\b",
      "complementos?\\b",
      "molhos?\\b",
      "cobertura",
      "maionese",
      "meia porcao",
    ]),
  ],
  [
    CATEGORY.COMBO,
    words(["combos?\\b", "combinados?\\b", "promoc", "rodizio"]),
  ],
  [
    CATEGORY.DRINK,
    words(["nao alcool", "sem alcool", "soft drink", "zero alcool"]),
  ],
  [
    CATEGORY.ALCOHOL,
    words([
      "cervej",
      "chopp",
      "long neck",
      "litrao",
      "vinhos?\\b",
      "espumante",
      "destilado",
      "doses?\\b",
      "cachac",
      "caipirinha",
      "caipiroska",
      "whisk",
      "gins?\\b",
      "vodka",
      "tequila",
      "rum\\b",
      "licor",
      "shots?\\b",
      "batidas?\\b",
      "coquetel",
      "drinks?\\b",
      "alcool",
      "skol",
      "adega",
      "bar\\b",
      "garrafas",
      // marcas, para classificar pelo nome do produto
      "heineken",
      "budweiser",
      "brahma",
      "stella",
      "corona",
      "amstel",
      "itaipava",
      "spaten",
      "eisenbahn",
      "petra",
      "devassa",
      "smirnoff",
      "absolut",
      "jack daniel",
      "red label",
      "black label",
      "white horse",
      "tanqueray",
      "ypioca",
    ]),
  ],
  [
    CATEGORY.DRINK,
    words([
      "bebida",
      "refri",
      "sucos?\\b",
      "aguas?\\b",
      "sodas?\\b",
      "energetic",
      "isotonic",
      "red bull",
      "monster",
      "chas?\\b",
      "cafes?\\b(?! da manha)",
      "cafeteria",
      "cappuccino",
      "espresso",
      "coados?\\b",
      "vitaminas?\\b",
      "smoothie",
      "chocolate quente",
      "add bebidas",
      // marcas
      "coca[- ]?cola",
      "coca\\b",
      "guarana",
      "fanta",
      "sprite",
      "pepsi",
      "schweppes",
      "h2oh",
      "del valle",
      "gatorade",
      "tonica",
    ]),
  ],
  [
    CATEGORY.MAIN,
    words([
      "pizza",
      "pastel",
      "pasteis",
      "pastelao",
      "esfiha",
      "esfirra",
      "tapioca",
      "crepe",
      "lanche",
      "hot dog",
      "calzone",
      "panqueca",
      "cafe da manha",
      "matinais",
    ]),
  ],
  [
    CATEGORY.DESSERT,
    words([
      "sobremes",
      "doces?\\b",
      "sorvete",
      "picole",
      "paletas?\\b",
      "chocolate",
      "bombon",
      "pudim",
      "brownie",
      "churros",
      "bolos?\\b",
      "confeitaria",
      "milk ?shake",
      "cascao",
      "copao",
      "guloseima",
      "balas\\b",
      "chiclete",
      "cupuacu",
      "acai",
      "cremes?\\b",
      "petit gateau",
      "cocada",
      "mousse",
      "torta doce",
    ]),
  ],
  [
    CATEGORY.SIDE,
    words([
      "porc(?:ao|oes)",
      "petisco",
      "entradas?\\b",
      "entradinha",
      "acompanhamento",
      "guarnic",
      "batatas? fritas?",
      "fritas\\b",
      "aperitivo",
      "belisco",
      "caldos?\\b",
      "caldinho",
      "saladas?\\b",
      "bolinhos?\\b",
      "pao de queijo",
      "elma chips",
      "salgados?\\b",
      "folhas",
      "onion rings?",
      "nuggets?",
    ]),
  ],
];

/** Categoria pelo nome de um grupo. */
export function classifyGroupName(name) {
  const text = normalize(name);
  if (!text || /^[\W\d_]*$/.test(text)) return CATEGORY.INTERNAL;
  if (GENERIC_NAMES.test(text)) return CATEGORY.GENERIC;
  for (const [category, pattern] of RULES) {
    if (pattern.test(text)) return category;
  }
  return CATEGORY.MAIN;
}

// Nome de produto com volume ("350ml", "2L", "600 ml") é bebida.
const VOLUME = /\b\d+(?:[.,]\d+)?\s?(?:ml|l|litros?)\b/;

/**
 * Categoria de um produto: a do grupo; se o grupo tem nome genérico, a do
 * nome do produto (ex.: "Coca-Cola 350ml" em "Fabricação Própria").
 */
export function classifyProduct(product, group) {
  const byGroup = classifyGroupName(group?.name);
  if (byGroup !== CATEGORY.GENERIC) return byGroup;

  const byName = classifyGroupName(product.name);
  if (byName !== CATEGORY.MAIN && byName !== CATEGORY.GENERIC) return byName;
  if (VOLUME.test(normalize(product.name))) return CATEGORY.DRINK;
  return CATEGORY.MAIN;
}
